// Carte de jeu cliquable pour le Dashboard Fan Zone
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Check, ChevronRight, Clock } from 'lucide-react';
import { DIFFICULTY_MULTIPLIERS } from '../services/fanZoneConfig';

const DifficultySelector = ({ difficulties, selectedDifficulty, onSelect, disabled }) => {
  if (!difficulties) return null;

  return (
    <div className="flex gap-2 mt-3">
      {Object.entries(difficulties).map(([key, config]) => {
        const isSelected = selectedDifficulty === key;
        const multiplier = DIFFICULTY_MULTIPLIERS[key];
        
        return (
          <button
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onSelect(key);
            }}
            disabled={disabled}
            className={`
              flex-1 py-2 px-3 rounded-xl text-xs font-medium transition-all duration-200
              ${isSelected 
                ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div>{config.label}</div>
            <div className="text-[10px] opacity-70">x{multiplier}</div>
          </button>
        );
      })}
    </div>
  );
};

const GameCard = ({ 
  game, 
  onClick, 
  isLocked = false,
  isCompleted = false,
  selectedDifficulty,
  onDifficultyChange
}) => {
  const [hovering, setHovering] = useState(false);
  
  const { icon, name, description, hasDifficulty, difficulties, frequency } = game;

  const handleClick = () => {
    if (!isLocked && !isCompleted && onClick) {
      onClick(selectedDifficulty);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: isLocked || isCompleted ? 1 : 1.02 }}
      whileTap={{ scale: isLocked || isCompleted ? 1 : 0.98 }}
      onHoverStart={() => setHovering(true)}
      onHoverEnd={() => setHovering(false)}
      onClick={handleClick}
      className={`
        relative overflow-hidden rounded-2xl p-5 transition-all duration-300
        ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}
        ${isCompleted ? 'cursor-default' : 'cursor-pointer'}
      `}
      style={{
        background: isCompleted 
          ? 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.05) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
        border: isCompleted 
          ? '1px solid rgba(16,185,129,0.3)'
          : '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Glow effect on hover */}
      <motion.div
        animate={{ opacity: hovering && !isLocked && !isCompleted ? 0.15 : 0 }}
        className="absolute inset-0 bg-gradient-to-r from-[#FFE400] to-[#BDFF00]"
      />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="text-4xl">{icon}</div>
          
          {isLocked && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-800/80 text-gray-400 text-xs">
              <Lock className="w-3 h-3" />
              <span>Verrouillé</span>
            </div>
          )}
          
          {isCompleted && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs">
              <Check className="w-3 h-3" />
              <span>Terminé</span>
            </div>
          )}
          
          {!isLocked && !isCompleted && frequency === 'daily' && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#FFE400]/10 text-[#FFE400] text-xs">
              <Clock className="w-3 h-3" />
              <span>Quotidien</span>
            </div>
          )}
        </div>
        
        {/* Title & Description */}
        <h3 className="text-lg font-bold text-white mb-1">{name}</h3>
        <p className="text-sm text-gray-400">{description}</p>
        
        {/* Difficulty selector */}
        {hasDifficulty && difficulties && !isCompleted && (
          <DifficultySelector
            difficulties={difficulties}
            selectedDifficulty={selectedDifficulty}
            onSelect={onDifficultyChange}
            disabled={isLocked}
          />
        )}
        
        {/* Play button */}
        {!isLocked && !isCompleted && (
          <motion.div 
            className="flex items-center justify-center gap-2 mt-4 py-2.5 rounded-xl font-semibold text-sm transition-colors"
            style={{
              background: 'linear-gradient(90deg, #FFE400, #BDFF00)',
              color: '#000'
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>Jouer</span>
            <ChevronRight className="w-4 h-4" />
          </motion.div>
        )}
        
        {isCompleted && (
          <div className="flex items-center justify-center gap-2 mt-4 py-2.5 rounded-xl font-semibold text-sm bg-emerald-500/20 text-emerald-400">
            <Check className="w-4 h-4" />
            <span>Défi complété aujourd'hui</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default GameCard;
